import type { FaceKey, TextNode } from '@repo/api-contracts';
import { FACE_KEYS } from '@repo/api-contracts';
import { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
  useDesignAssetUrls,
  useDesignQuery,
  useFontFaces,
  useProductTextOptionsQuery,
} from '@/entities/design';
import { useLatestPublicEditAreaQuery } from '@/entities/edit-area';
import { useStoreProductDetailQuery } from '@/entities/product';
import { useAuthStore } from '@/entities/user';
import {
  type BoxNode,
  clearDraftPreview,
  collectDocumentAssetIds,
  collectFontIds,
  commitNodeChange,
  createDefaultTextNode,
  createImageNodeFitted,
  faceBackdropOf,
  findNode,
  hasAnyLayer,
  hitTestPreview,
  insertNode,
  nudge,
  saveDraft,
  saveDraftPreview,
  sizeTextNode,
  updateNode,
  useDraftPreviewSnapshot,
  useEditorShortcuts,
  useEditorStore,
  useInitializeEditor,
  useSaveDesign,
  useWarpedPreview,
} from '@/features/design-editor';
import type { UploadedDesignImage } from '@/features/design-image-upload';
import { guestQuoteRequestPath, quoteRequestPath } from '@/shared/config';
import { createCanvasContext, createCanvasMeasure, getOrCreateGuestKey } from '@/shared/lib';
import { useCustomizeMaterials } from './use-customize-materials';

const EMPTY_TEXT_OPTIONS = { fonts: [], inkColors: [] } as const;
const FLAT_PX_PER_MM = 4;

const isNotFoundLike = (code: unknown): boolean => code === 'NOT_FOUND' || code === 'BAD_REQUEST';

interface UseCustomizeEditorInput {
  readonly productId: string;
  readonly designId: string | null;
  /** 상세에서 고른 색상(`?color=`) — 새 문서를 열 때만 처음 색상 후보로 쓴다. */
  readonly preferredColorId?: string | null;
}

/**
 * 고객 커스터마이징 화면의 데이터·동작 — 읽기(상품·편집영역·폰트/잉크·저장 디자인·에셋 URL) → 스토어 초기화 →
 * 재료·미리보기·저장을 묶고, 진입 분기(준비 중·없음·mesh·실패)를 판정한다. 화면 배치는 CustomizeEditor 가 맡는다.
 */
export const useCustomizeEditor = ({
  productId,
  designId,
  preferredColorId = null,
}: UseCustomizeEditorInput) => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const productQuery = useStoreProductDetailQuery(productId);
  const editAreaQuery = useLatestPublicEditAreaQuery(productId);
  const textOptionsQuery = useProductTextOptionsQuery(productId);
  const designQuery = useDesignQuery(designId, { enabled: isAuthenticated });
  // 비회원은 이 브라우저의 비회원 키로 이미지를 올리고 표시 URL 을 받는다(G6). 회원은 null — 키를 만들지 않는다.
  const guestKey = useMemo(
    () => (isAuthenticated ? null : getOrCreateGuestKey()),
    [isAuthenticated],
  );
  const store = useEditorStore();
  const lastBackdropRefresh = useRef(0);
  const measure = useMemo(() => createCanvasMeasure(), []);
  const definition = editAreaQuery.data?.definition ?? null;
  const textOptions = textOptionsQuery.data ?? EMPTY_TEXT_OPTIONS;
  // 문서가 쓰는 폰트만 로드한다 — 텍스트를 넣거나 폰트를 바꾸면 그 폰트를 그때 받는다.
  const neededFontIds = useMemo(() => collectFontIds(store.document), [store.document]);
  const fonts = useFontFaces(textOptions.fonts, neededFontIds);
  const fontIds = useMemo(() => textOptions.fonts.map((font) => font.id), [textOptions.fonts]);
  const assetIds = useMemo(() => collectDocumentAssetIds(store.document), [store.document]);
  const assetUrls = useDesignAssetUrls(assetIds, { enabled: true, guestKey });

  // 스토어 초기화 — 상품별로 한 번(저장 디자인 id 가 다를 때만 다시). 한 번 되면 캔버스를 내리지 않는다.
  const { initialized, restoredDraft } = useInitializeEditor({
    productId,
    definition,
    colorOptions: productQuery.data?.colorOptions,
    savedDesign: designQuery.data,
    designId,
    preferredColorId,
    isAuthenticated,
  });

  const face = definition?.faces[store.faceKey];
  const placement =
    face?.placements.find((candidate) => candidate.componentId === store.componentId) ??
    face?.placements[0];
  const surface = face?.surfaces.find((candidate) => candidate.id === placement?.surfaceId) ?? null;
  const component =
    definition?.components.find((candidate) => candidate.id === placement?.componentId) ?? null;
  const colorEntry = definition?.colors.find(
    (entry) => entry.colorOptionId === store.document.colorOptionId,
  );
  const photoSize = colorEntry?.faces[store.faceKey]?.photoSize ?? null;
  const { materials, guide } = useCustomizeMaterials({
    assetUrls: assetUrls.urls,
    fonts,
    fontIds,
    inkColors: textOptions.inkColors,
    // 안내선은 노드를 선택해 편집하는 동안에만 받는다.
    guideUrl: store.selectedNodeId !== null ? (face?.composite?.safeAreaUrl ?? null) : null,
    surface,
    photo: photoSize,
    component,
    position: placement?.position ?? null,
    pxPerMm: FLAT_PX_PER_MM,
  });
  // 곡면(원통) 면은 평면 겹침으로 바로 편집하고, 손을 멈추면 실제 곡면 합성을 뒤에서 그려 바꿔 보여 준다.
  const warped = useWarpedPreview({
    enabled: surface !== null && surface.type !== 'flat',
    definition,
    revision: store.revision,
    faceKey: store.faceKey,
    colorOptionId: store.document.colorOptionId,
    body: { components: store.document.components },
    assetUrls: assetUrls.urls,
    assetUrlsPending: assetUrls.isPending,
    fonts,
    inkColors: textOptions.inkColors,
    fontIds,
    refreshAssetUrls: assetUrls.refresh,
    refreshEditArea: async () => {
      await editAreaQuery.refetch();
    },
  });
  // 비회원 [견적 요청하기] 때 요청 화면에 보여 줄 앞면 미리보기 — 손을 멈추면 앞면을 뒤에서 그려 둔다(G12).
  const previewSnapshot = useDraftPreviewSnapshot({
    enabled: !isAuthenticated && initialized,
    definition,
    revision: store.revision,
    colorOptionId: store.document.colorOptionId,
    body: { components: store.document.components },
    assetUrls: assetUrls.urls,
    assetUrlsPending: assetUrls.isPending,
    fonts,
    inkColors: textOptions.inkColors,
    fontIds,
    refreshAssetUrls: assetUrls.refresh,
    refreshEditArea: async () => {
      await editAreaQuery.refetch();
    },
  });
  const saver = useSaveDesign({ productId });
  useEditorShortcuts();

  const retryAll = () => {
    void editAreaQuery.refetch();
    void productQuery.refetch();
    void textOptionsQuery.refetch();
    void designQuery.refetch();
  };

  if (productQuery.isPending || editAreaQuery.isPending || textOptionsQuery.isPending)
    return { status: 'loading', message: '커스터마이징 화면을 준비하는 중입니다.' } as const;
  if (
    productQuery.isError ||
    editAreaQuery.isError ||
    textOptionsQuery.isError ||
    definition === null
  ) {
    const notFound =
      isNotFoundLike(editAreaQuery.error?.data?.code) ||
      isNotFoundLike(productQuery.error?.data?.code);
    return {
      status: 'unavailable',
      kind: notFound ? 'not-found' : 'error',
      onRetry: retryAll,
    } as const;
  }
  const hasMesh = Object.values(definition.faces).some((entry) =>
    entry?.placements.some(
      (candidate) => entry.surfaces.find((s) => s.id === candidate.surfaceId)?.type === 'mesh',
    ),
  );
  if (hasMesh) return { status: 'unavailable', kind: 'mesh', onRetry: undefined } as const;
  // 저장 디자인 진입 실패(스펙 §9) — 초기화 전에만. 저장 뒤 재조회 실패로 편집 중인 캔버스를 내리지 않는다.
  if (!initialized && designId !== null && isAuthenticated && designQuery.isError)
    return {
      status: 'unavailable',
      kind: isNotFoundLike(designQuery.error?.data?.code) ? 'design-not-found' : 'error',
      onRetry: () => {
        void designQuery.refetch();
      },
    } as const;
  if (!initialized) return { status: 'loading', message: '디자인을 불러오는 중입니다.' } as const;

  const product = productQuery.data;
  const colors = product.colorOptions.map(({ id, name, colorCode, isAvailable }) => ({
    id,
    name,
    colorCode,
    isAvailable,
  }));
  const found =
    store.selectedNodeId === null ? null : findNode(store.document, store.selectedNodeId);
  const selected = found?.node ?? null;
  const selectedArea =
    found === null
      ? null
      : (definition.components.find((candidate) => candidate.id === found.componentId) ?? null);
  const defaultFont = textOptions.fonts[0];
  const defaultInk = textOptions.inkColors[0];
  const textDisabledReason =
    component === null
      ? '이 면에는 인쇄 영역이 없습니다.'
      : defaultFont === undefined || defaultInk === undefined
        ? '이 상품은 텍스트를 넣을 수 없습니다.'
        : null;
  const imageDisabledReason = component === null ? '이 면에는 인쇄 영역이 없습니다.' : null;
  const sizeText = (node: TextNode): TextNode => sizeTextNode(node, fonts.familyOf, measure);
  const faces = FACE_KEYS.filter((key): key is FaceKey => definition.faces[key] !== undefined);
  const faceOptions = faces.map((key) => ({
    face: key,
    hasLayers: (definition.faces[key]?.placements ?? []).some((candidate) =>
      store.document.components.some(
        (entry) => entry.componentId === candidate.componentId && entry.layers.length > 0,
      ),
    ),
  }));

  /** 텍스트를 지금 면 인쇄 영역 가운데에 넣고 고른다. 넣은 노드 id(못 넣으면 null)를 돌려준다. */
  const addText = (): string | null => {
    if (component === null || defaultFont === undefined || defaultInk === undefined) return null;
    const node = sizeText(
      createDefaultTextNode({
        id: crypto.randomUUID(),
        fontId: defaultFont.id,
        inkColorId: defaultInk.id,
        component,
      }),
    );
    store.mutate((doc) => insertNode(doc, component.id, node));
    store.select(node.id);
    return node.id;
  };

  const addImage = (image: UploadedDesignImage): void => {
    if (component === null) return;
    const node = createImageNodeFitted({
      id: crypto.randomUUID(),
      assetId: image.assetId,
      widthPx: image.widthPx,
      heightPx: image.heightPx,
      component,
    });
    store.mutate((doc) => insertNode(doc, component.id, node));
    store.select(node.id);
  };

  const patchText = (patch: Partial<TextNode>): void => {
    const nodeId = store.selectedNodeId;
    if (nodeId === null) return;
    store.mutate((doc) =>
      updateNode(doc, nodeId, (node) =>
        node.type === 'text' ? sizeText({ ...node, ...patch }) : node,
      ),
    );
  };

  const save = () => {
    void saver.save();
  };

  /**
   * 견적 요청 — 회원은 저장한 뒤(변경이 없으면 바로) 이 디자인의 견적 요청 화면(/request/:designId)으로 간다.
   * 비회원은 저장이 없다(G5) — 계약 검사만 하고 임시 작업과 앞면 미리보기 그림을 이 기기(localStorage)에 남긴 뒤
   * 비회원 요청 화면(/request/guest?productId&colorId&qty, G12)으로 간다. 잘못된 레이어가 있으면 저장 오류와 같은
   * 안내만 남기고 머문다. 미리보기 그림은 있으면 좋은 부가 정보라 아직 못 그렸거나 못 남겨도 이동을 막지 않는다.
   * 패널에서 고른 올바른 수량이 있으면 ?qty= 로 넘겨 요청 폼의 처음 수량으로 잇는다.
   */
  const requestQuote = async (qty: number | null): Promise<void> => {
    const current = useEditorStore.getState();
    if (!isAuthenticated) {
      if (!saver.validate()) return;
      saveDraft(productId, current.document);
      const preview = previewSnapshot.capture(current.revision);
      // 이전 요청에서 남은 낡은 그림을 먼저 치운다 — 새 그림을 못 만들었거나 저장이 용량 초과로 실패해도 다른 디자인의 그림이
      // 요청 화면에 보이면 안 된다.
      clearDraftPreview(productId);
      if (preview !== null) saveDraftPreview(productId, preview);
      void navigate(
        guestQuoteRequestPath({
          productId,
          colorOptionId: current.document.colorOptionId,
          quantity: qty,
        }),
      );
      return;
    }
    if (current.designId !== null && !current.dirty) {
      void navigate(quoteRequestPath(current.designId, qty));
      return;
    }
    const outcome = await saver.save();
    if (outcome.status === 'saved') void navigate(quoteRequestPath(outcome.designId, qty));
  };

  return {
    status: 'ready',
    product,
    definition,
    colors,
    textOptions,
    measure,
    materials,
    guide,
    backdrop:
      component === null
        ? null
        : faceBackdropOf({
            definition,
            colorOptionId: store.document.colorOptionId,
            faceKey: store.faceKey,
            componentId: component.id,
          }),
    restoredDraft,
    /** 회원만 [저장]이 있다(G5) — 비회원은 [견적 요청하기]만. */
    canSave: isAuthenticated,
    /** 비회원 키(업로드 버튼이 쓴다). 회원은 null. */
    guestKey,
    saver,
    store,
    selected,
    selectedArea,
    component,
    faceOptions,
    textDisabledReason,
    imageDisabledReason,
    canReset: hasAnyLayer(store.document),
    warped: warped.bitmap,
    warpedRenderMs: warped.renderMs,
    createContext: createCanvasContext,
    actions: {
      addText,
      addImage,
      patchText,
      save,
      requestQuote: (qty: number | null) => {
        void requestQuote(qty);
      },
      /** 곡면 합성 위 클릭(사진 px) → 역워프로 고른 레이어 id(최상위 노드). 없으면 null. */
      warpedHit: (point: { x: number; y: number }): string | null => {
        if (photoSize === null) return null;
        const hit = hitTestPreview({
          definition,
          faceKey: store.faceKey,
          body: { components: store.document.components },
          photo: photoSize,
          point,
        });
        if (hit === null) return null;
        if (hit.componentId !== store.componentId) store.setComponent(hit.componentId);
        return hit.nodeId;
      },
      /** 목업 사진 서명 URL 이 만료돼 못 그렸을 때 — 편집영역을 다시 받아 새 URL 로 그린다. */
      refreshBackdrop: () => {
        // 사진이 계속 깨져도 재조회가 꼬리를 물지 않게 30초에 한 번만 다시 받는다.
        const now = Date.now();
        if (now - lastBackdropRefresh.current < 30_000) return;
        lastBackdropRefresh.current = now;
        void editAreaQuery.refetch();
      },
      /** 면 전환 — 선택이 풀리고 그 면의 목업 사진 위에서 바로 이어 편집한다. */
      changeFace: (next: FaceKey) => {
        store.setFace(
          next,
          definition.faces[next]?.placements[0]?.componentId ?? store.componentId,
        );
        useEditorStore.getState().clearSelection();
      },
      changeNode: (node: BoxNode) =>
        store.mutate((doc) => commitNodeChange(doc, node, fonts.familyOf, measure)),
      nudgeSelected: (dx: number, dy: number) => {
        const nodeId = store.selectedNodeId;
        if (nodeId === null) return;
        store.mutate((doc) =>
          updateNode(doc, nodeId, (node) => (node.type === 'group' ? node : nudge(node, dx, dy))),
        );
      },
    },
  } as const;
};

/** 편집 화면을 그릴 준비가 된 상태 — 스테이지·주문 패널이 받는다. */
export type CustomizeEditorReady = Extract<
  ReturnType<typeof useCustomizeEditor>,
  { status: 'ready' }
>;
